/**
 * @param {React.ReactNode} children - content
 * @param {string} id - a unique ID for the drawer
 * @param {Function} [onClose] - close action
 * @param {Function} [okOK] - ok action
 * @param {string<left|right|bottom|top>} [direction] - slide in from, default bottom
 * @param {string<ok|okCancel|custom>} [type] - type of button, default ok, if custom, it will only display 'children'
 * @param {string} [title] - drawer title
 * @param {string} [okLabel] - OK button label, default 'OK'
 * @param {string} [cancelLabel] - Cancel button label, default 'Cancel'
 * @returns {React.ReactNode}
 */
import React, { useState, useEffect, useContext, createContext, useRef } from 'react';
import { StyleSheet, View, Animated, useWindowDimensions, Pressable, TouchableWithoutFeedback } from 'react-native';
import { themeConfig } from '../styles/themeConfig';
import { BText } from './BText';
import { ButtonContainer, Button } from './Form';

export interface DrawerProps {
  id: string;
  visible: boolean;
  hideButton?: boolean;
  onClose?: () => void;
  onOK?: () => void;
  direction?: 'left' | 'right' | 'bottom' | 'top';
  type?: 'ok' | 'onCancel' | 'custom';
  okLabel?: string;
  cancelLabel?: string;
  title?: string;
  children: React.ReactNode;
  [x: string]: any;
}

type DrawerContextProps = {
  render: Function;
  getNextIndex: Function;
  getDrawer: Function;
};

export const DrawerContext = createContext<DrawerContextProps>({
  render: () => null,
  getNextIndex: () => null,
  getDrawer: () => null,
});

// use in component
export const Drawer = (props: DrawerProps) => {
  const ctx = useContext(DrawerContext);
  const [id, setID] = useState<string | null>(null);

  useEffect(() => {
    setID(props.id || (Math.random() * 1000).toString());
  }, []);

  useEffect(() => {
    if (id && props) {
      let newZIndex = 0;
      // get old values
      const oldDrawer = ctx.getDrawer(id);
      if (props.visible && (!oldDrawer || (oldDrawer && !oldDrawer.visible))) {
        // only new "visible" will update zIndex
        console.log(id);
        newZIndex = ctx.getNextIndex();
      }

      ctx.render({ ...props, zIndex: newZIndex, id: id });
    }
  }, [props]);

  return <></>;
};

// use in App
type DrawerProviderProps = {
  children: React.ReactNode;
};
export const DrawerProvider = ({ children }: DrawerProviderProps) => {
  const [drawers, setDrawers] = useState<DrawerProps[]>([]);
  const zIndex = useRef<number>(100);

  const renderDrawer = (drawer: DrawerProps) => {
    setDrawers((prevList: DrawerProps[]) => {
      const newList = [...prevList];
      const index = newList.findIndex((item: DrawerProps) => item.id === drawer.id);
      if (index !== -1) {
        newList[index] = drawer;
      } else {
        newList.push(drawer);
      }
      return newList;
    });
  };

  const getDrawer = (id: string) => {
    return drawers.find(d => d.id === id);
  };

  const getNextIndex = () => {
    const newIndex = zIndex.current + 1;
    zIndex.current++;
    return newIndex;
  };

  return (
    <DrawerContext.Provider
      value={{
        render: renderDrawer,
        getNextIndex: getNextIndex,
        getDrawer: getDrawer,
      }}
    >
      {children}
      {drawers.length > 0 &&
        drawers.map(({ children: subChildren, ...drawer }: DrawerProps, idx: number) => {
          return (
            <DrawerRender key={idx} {...drawer}>
              {subChildren}
            </DrawerRender>
          );
        })}
    </DrawerContext.Provider>
  );
};

// Local Render - call from provider, not from other component
const DrawerRender = ({
  visible,
  hideButton = false,
  onClose = () => null,
  onOK = () => null,
  direction = 'bottom',
  type = 'ok',
  okLabel = 'OK',
  cancelLabel = 'Cancel',
  title = '',
  children,
  zIndex = 100,
}: DrawerProps) => {
  const { width, height } = useWindowDimensions();
  const [animation] = useState(new Animated.Value(0));
  const [isOpen, setIsOpen] = useState(visible);

  useEffect(() => {
    if (visible) {
      setIsOpen(true);
    }

    Animated.timing(animation, {
      toValue: visible ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      if (!visible) {
        setIsOpen(false);
      }
    });
  }, [animation, visible]);

  const getTransform = (): any => {
    const maxDistance = direction === 'left' || direction === 'right' ? width : height;
    const initialValue = direction === 'left' || direction === 'top' ? -maxDistance : maxDistance;
    const finalValue = 0;

    return {
      transform: [
        {
          [direction === 'left' || direction === 'right' ? 'translateX' : 'translateY']: animation.interpolate({
            inputRange: [0, 1],
            outputRange: [initialValue, finalValue],
          }),
        },
      ],
    };
  };

  const handleOK = () => {
    if (onOK) {
      onOK();
    }
    handleClose();
  };

  const handleClose = () => {
    if (onClose) onClose();
  };
  return (
    <>
      {isOpen && (
        <View style={{ ...StyleSheet.absoluteFillObject, flex: 1, zIndex: zIndex }}>
          <Animated.View style={[styles.container, getTransform()]}>
            <TouchableWithoutFeedback onPress={handleClose} style={styles.pressContainer}>
              <View style={styles.overlay} />
            </TouchableWithoutFeedback>
            {type !== 'custom' && (
              <View style={[styles.drawerContainer, { width: width }]}>
                <View style={styles.drawerHeader}>
                  <Pressable onPress={handleClose} style={styles.pressContainer}>
                    <View style={styles.drawerClose} />
                  </Pressable>
                </View>
                {title !== '' && <BText style={styles.drawerTitle}>{title}</BText>}
                <View style={styles.drawerContent}>{children}</View>
                {!hideButton && (
                  <View style={styles.drawerButtonContainer}>
                    <ButtonContainer direction="horizontal">
                      <Button onPress={handleOK}>{okLabel}</Button>
                      {type === 'onCancel' && (
                        <Button onPress={handleClose} colorName="grey">
                          {cancelLabel}
                        </Button>
                      )}
                    </ButtonContainer>
                  </View>
                )}
              </View>
            )}
            {type === 'custom' && <>{children}</>}
          </Animated.View>
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    //zIndex: 2,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    width: '100%',
    height: '100%',
    flex: 1,
  },
  pressContainer: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 2,
  },
  drawerContainer: {
    position: 'absolute',
    zIndex: 3,
    flex: 1,
    bottom: 0,
    //marginLeft: 8,
    //marginRight: 'auto',
    height: 'auto',
    backgroundColor: themeConfig.colors.white,
    padding: 16,
    paddingTop: 8,
    paddingBottom: 8,
    borderRadius: themeConfig.settings.borderRadius,
    elevation: 4,
    alignContent: 'flex-end',
    flexDirection: 'column',
  },
  drawerHeader: {
    height: 5,
  },
  drawerClose: {
    backgroundColor: themeConfig.colors.grey,
    width: 50,
    height: 5,
    margin: 'auto',
    borderRadius: themeConfig.settings.borderRadius,
  },
  drawerTitle: {
    fontWeight: 'bold',
    fontSize: 16,
    marginTop: 16,
  },
  drawerContent: {
    paddingTop: 16,
    paddingBottom: 32,
  },
  drawerButtonContainer: {
    width: '100%',
    marginBottom: -10,
  },
});
